import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from '@tanstack/react-router';
import { Trophy, MessageSquare, Users, FolderOpen, BookOpen, GraduationCap, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import heroImage from '../Assets/Home/1.png';
export default function HAmePage() {
  const navigate = useNavigate();
  const [ scrollY, setScrollY ] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: MessageSquare,
      title: 'Smart, Layered Communication',
      description: ' From private messages to department-wide updates, campus-wide announcements, and central administration broadcasts, UNILinkd ensures every message reaches exactly where it should.',
    },
    {
      icon: Users,
      title: 'Unified Groups with Dedicated Channels',
      description: 'Create structured channels for subjects, projects, and discussions — keeping communication organized and distraction-free.',
    },
    {
      icon: FolderOpen,
      title: 'Intelligent File Manager',
      description: 'Store and circulate notes in categorized drawers and maintain a clean, searchable academic repository.',
    },
    {
      icon: BookOpen,
      title: 'Integrated E-Library',
      description: 'Access department resources, campus libraries, global publications, and shared academic material — all within one unified digital space.',
    },
    {
      icon: GraduationCap,
      title: 'Campus Clubs',
      description: 'A focused internal network where students build communities, explore interests, and create opportunities — making extracurricular engagement seamless and fun.',
    },
    {
      icon: Trophy,
      title: 'Elaborate Placement Portal',
      description: 'A specialized space connecting students, alumni, and recruiters — simplifying internship access, company applications, and campus placement processes.',
    },
  ];

  return (
    <div className="flex flex-col  bg-brand-yellow">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] md:py-16 py-12 bg-brand-yellow py-12 md:py-16">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            backgroundImage: 'radial-gradient(circle, #004CFF 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="container relative z-10">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <div className="flex flex-col  justify-center space-y-6">
              <div className="inline-flex items-center space-x-2">
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-pulse rounded-full bg-brand-white shadow-lg"></div>
                  <div className="h-0.5 w-12 bg-gradient-to-r from-brand-black to-transparent"></div>
                  <div className="h-4 w-4 animate-pulse rounded-full bg-brand-white shadow-lg" style={{ animationDelay: '0.2s' }}></div>
                  <div className="h-0.5 w-12 bg-gradient-to-r from-brand-black to-transparent"></div>
                  <div className="h-4 w-4 animate-pulse rounded-full bg-brand-white shadow-lg" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
              <h1 className="text-5xl font-extrabold tracking-tight text-brand-black sm:text-6xl md:text-7xl">
                Your Custom Cloud Campus{' '}
                {/**
                <span className="bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-brand-black">
                  Offline to Digital
                </span>
                */}
              </h1>
              <p className="text-xl font-medium text-brand-black md:text-2xl">
                UNILinkd centralizes communication, collaboration, and campus engagement into one streamlined system. Every message matters. Every update reaches. Every conversation stays connected
              </p>
              <div className="flex flex-col  gap-4 sm:flex-row">
                <Button
                  size="lg"
                  onClick={() => navigate({ to: '/connect' })}
                  className="group rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate({ to: '/prototypes' })}
                  className="rounded-xl bg-brand-white text-lg font-bold text-brand-black shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                >
                  View Prototypes
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-brand-blue to-[#0066FF] opacity-20 blur-2xl"></div>
              <img
                src={heroImage}
                alt="Education Bridge"
                className="relative rounded-3xl shadow-2xl transition-transform hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section
        className="relative bg-brand-white py-12"
        style={{
          transform: `translateY(${scrollY * 0.1}px)`,
        }}
      >
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            {/*}<h2 className="mb-4 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl text-brand-black">Our Mission</h2>*/}
            <p className="text-xl font-medium text-brand-black">
              Because communication shouldn’t get lost between corridors and inboxes.
              UNILinkd connects everyone and everything — from casual chats to major announcements
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-yellow bg-brand-yellow to-[#FFE566] py-12 mt-20 mb-0">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            backgroundImage: 'linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF), linear-gradient(45deg, #004CFF 25%, transparent 25%, transparent 75%, #004CFF 75%, #004CFF)',
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px',
          }}
        />
        <div className="container relative z-10 ">
          <div className="mb-8 text-center">
            <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl text-brand-black">Core Features</h2>
            <p className="text-xl font-medium text-brand-black">
              Digital Campus Infrastructure that includes - All features
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group rounded-3xl bg-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <CardHeader>
                  <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-[#0066FF] shadow-lg transition-transform group-hover:scale-110 group-hover:rotate-3">
                    <feature.icon className="h-8 w-8 text-brand-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-brand-black">{feature.title}</CardTitle>
                  <CardDescription className="text-base font-medium text-brand-black">{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="relative bg-transparent py-12"
      //style={{transform: `translateY(${scrollY * 0.15}px)`,}}
      >
        <div className="container">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-yellow via-brand-yellow to-[#FFE566] p-8 text-center shadow-2xl md:p-10">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, #004CFF 0%, transparent 50%), radial-gradient(circle at 80% 80%, #004CFF 0%, transparent 50%)',
            }}></div>
            <div className="relative z-10">
              <h2 className="mb-3 bg-gradient-to-r from-brand-blue to-[#0066FF] bg-clip-text text-4xl font-extrabold tracking-tight text-brand-black md:text-5xl">
                Turn your campus into a smart campus today
              </h2>
              <p className="mb-6 text-xl font-medium text-brand-black">
                Join thousands of students and educators already using UNILinkd
              </p>
              <div className="flex flex-col  gap-4 sm:flex-row sm:justify-center">
                <Button
                  size="lg"
                  onClick={() => navigate({ to: '/download' })}
                  className="rounded-xl bg-gradient-to-r from-brand-blue to-[#0066FF] text-lg font-bold text-brand-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Download Now
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate({ to: '/connect' })}
                  className="rounded-xl bg-brand-white text-lg font-bold text-brand-black shadow-lg transition-all hover:scale-105 hover:shadow-xl"
                >
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
